import { ReactElement } from 'react';
import { ATMOSPHERIC_SOURCE_LABEL, AirDensitySource, AtmosphericSourceEnum } from '../../../../../types/Run';

interface RunDensityRow {
  id: string;
  runNumber: number | null;
  configLetter: string | null;
  description: string | null;
  airDensity: number | null;
  synthetic: boolean;
  sources: AirDensitySource | null;
}

const sourceLabel = (value: number | undefined): string =>
  value === undefined ? '—' : (ATMOSPHERIC_SOURCE_LABEL[value as AtmosphericSourceEnum] ?? String(value));

const extractRows = (session: any): RunDensityRow[] => {
  const rows: RunDensityRow[] = [];
  for (const config of session?.configs ?? []) {
    for (const run of config?.runs ?? []) {
      const dp = run?.solve?.selections?.dynamic_pressure;
      rows.push({
        id: run?.id ?? `${config?.letter}-${run?.run_number}`,
        runNumber: run?.run_number ?? null,
        configLetter: config?.letter ?? null,
        description: run?.description ?? null,
        airDensity: typeof run?.air_density_used === 'number' ? run.air_density_used : null,
        synthetic: Boolean(dp?.synthetic),
        sources: dp?.air_density ?? null,
      });
    }
  }
  rows.sort((a, b) => (a.runNumber ?? 0) - (b.runNumber ?? 0));
  return rows;
};

/**
 * Read-only per-run air density + provenance. The density is the value the
 * CdA calc actually used (server `air_density_used`); the source badges come
 * from the run's `solve.selections.dynamic_pressure` stamp.
 */
export default function RunDensityTable({ session }: { session: any }): ReactElement | null {
  const rows = extractRows(session);
  if (rows.length === 0 || rows.every((r) => r.airDensity === null && r.sources === null)) {
    return null;
  }

  return (
    <div className="cr-mt-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
      <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Air Density per Run</h5>
      <div className="cr-overflow-x-auto">
        <table className="cr-min-w-full cr-text-sm">
          <thead>
            <tr className="cr-text-left cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-500">
              <th className="cr-py-2 cr-pr-4">Run</th>
              <th className="cr-py-2 cr-pr-4">ρ (kg/m³)</th>
              <th className="cr-py-2 cr-pr-4">Dyn. pressure</th>
              <th className="cr-py-2 cr-pr-4">Temp</th>
              <th className="cr-py-2 cr-pr-4">Baro</th>
              <th className="cr-py-2">Humidity</th>
            </tr>
          </thead>
          <tbody className="cr-divide-y cr-divide-gray-200">
            {rows.map((row) => (
              <tr key={row.id} className="cr-text-gray-700">
                <td className="cr-whitespace-nowrap cr-py-2 cr-pr-4">
                  {row.configLetter ?? ''}{row.runNumber ?? '—'}
                </td>
                <td className="cr-py-2 cr-pr-4 cr-font-medium">
                  {row.airDensity != null ? row.airDensity.toFixed(4) : '—'}
                </td>
                <td className="cr-py-2 cr-pr-4">
                  {row.synthetic ? (
                    <span
                      className="cr-rounded-full cr-bg-amber-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-amber-800"
                      title="No wind-sensor dynamic pressure — synthesized as 0.5·ρ·v² from speed"
                    >
                      Synthetic
                    </span>
                  ) : (
                    <span className="cr-rounded-full cr-bg-emerald-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-emerald-800">
                      Measured
                    </span>
                  )}
                </td>
                <td className="cr-whitespace-nowrap cr-py-2 cr-pr-4 cr-text-xs cr-text-gray-500">
                  {sourceLabel(row.sources?.temperature)}
                </td>
                <td className="cr-whitespace-nowrap cr-py-2 cr-pr-4 cr-text-xs cr-text-gray-500">
                  {sourceLabel(row.sources?.barometric_pressure)}
                </td>
                <td className="cr-whitespace-nowrap cr-py-2 cr-text-xs cr-text-gray-500">
                  {sourceLabel(row.sources?.humidity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
